/**
 * World-space edges of the painted guidance lane (ADR-065), as the renderer
 * draws them — pure geometry, no Babylon, so the SAME polylines the player
 * sees can be checked by tests: no jumps, no folds, no twists, on any map.
 *
 * The lane's CENTRE line is built in world space from the road pieces
 * (src/sim/lane_ribbon.ts); this module only extrudes it to two edges.
 */

export interface GuideEdges {
  /** Centre samples, every ~0.5 m along the lane. */
  cx: number[];
  cz: number[];
  /** Left / right edge points (x, z), same sampling. */
  L: Array<[number, number]>;
  R: Array<[number, number]>;
  /** Provenance of each centre sample: the road-network piece index it was
   * ruled from, or -1 for a join arc, a straight stitch, or a pedestrian
   * crossing window (where the lane is route-parametrised, not piece-ruled). */
  piece: number[];
}

/**
 * Extrude a centre polyline to two edges with CHORD normals, so a mitered
 * stroke cannot shear. Inside a tight bend an offset edge would fold back on
 * itself; an edge point that travels backwards along the lane collapses onto
 * its predecessor, so the fold becomes a clean cusp.
 */
export function extrudeEdges(
  cx: readonly number[],
  cz: readonly number[],
  half: readonly number[],
  piece: readonly number[],
): GuideEdges {
  const n = cx.length;
  const L: Array<[number, number]> = [];
  const R: Array<[number, number]> = [];
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - 1);
    const b = Math.min(n - 1, i + 1);
    const yaw = Math.atan2(cx[b]! - cx[a]!, cz[b]! - cz[a]!);
    const nx = Math.cos(yaw);
    const nz = -Math.sin(yaw);
    const h = Math.max(0.18, half[i]!);
    L.push([cx[i]! - nx * h, cz[i]! - nz * h]);
    R.push([cx[i]! + nx * h, cz[i]! + nz * h]);
  }
  for (const E of [L, R]) {
    for (let i = 1; i < n; i++) {
      const a = Math.max(0, i - 1);
      const b = Math.min(n - 1, i + 1);
      const tx = cx[b]! - cx[a]!;
      const tz = cz[b]! - cz[a]!;
      const dx = E[i]![0] - E[i - 1]![0];
      const dz = E[i]![1] - E[i - 1]![1];
      if (dx * tx + dz * tz < 0) E[i] = [E[i - 1]![0], E[i - 1]![1]];
    }
  }
  return { cx: [...cx], cz: [...cz], L, R, piece: [...piece] };
}
